'use client';

import { c as _c } from "react/compiler-runtime";
import { FileText, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { SecaoAutenticidade, SecaoCapa, SecaoObjetivo, SecaoOQueEDocumento, SecaoTextosIntro, TabelaPartesGrupo } from '@/features/arbitral-detalhes';
import { DocumentoPaginado, DocTituloSecao, MarcadorSecao, SumarioDocumento, type BlocoDocumento } from '@/shared/components/documentos/paginado';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import type { CampoPersonalizadoDoCertificado, CertificadoNegociacaoDocumentoProps, CertificadoNegociacaoResumoDto, ContatoDoCertificado, ConversaDoCertificado, MarcoDoCertificado, SecaoDoDocumentoDoCertificado, SecoesDoDocumentoDoCertificado, TrechoDoDocumentoDoCertificado, UrlDeValidacaoExibivel } from '../../../types/certificadoNegociacaoTypes';
import type { MarcoNegociacao } from '../../../types/negociacaoMarcoTypes';
import { contatosDaContraparte, dataCertificado, dividirTextoCertificado, FATIA_DA_CELULA, FATIA_DA_DESCRICAO_DO_MARCO, urlDeValidacaoExibivel } from '../../../utils/certificadoNegociacao';
import { agruparMarcosPorDia } from '../../../utils/marcoDataHora';
import { MarcoCard } from '../linhaDoTempo/MarcoCard';
import { blocosConversasCertificado, blocosTabelaCertificado } from './CertificadoNegociacaoBlocos';
const SECOES = [{
  numero: 1,
  titulo: 'Objetivo e escopo',
  descricao: 'O que este documento registra'
}, {
  numero: 2,
  titulo: 'Caso e participantes',
  descricao: 'Identificação da negociação e das partes'
}, {
  numero: 3,
  titulo: 'Contatos e linha do tempo',
  descricao: 'Números utilizados e marcos da negociação'
}, {
  numero: 4,
  titulo: 'Conversas registradas',
  descricao: 'Conversas completas, organizadas por contato'
}, {
  numero: 5,
  titulo: 'Segurança e autenticidade',
  descricao: 'Autenticação na emissão e conferência pública'
}];
const numeroDaNegociacao = ({
  caso
}: CertificadoNegociacaoResumoDto): string => caso.numeroNegociacao ?? 'Número não informado';
const trechoCompleto = (blocos: BlocoDocumento[]): TrechoDoDocumentoDoCertificado => ({
  blocos,
  completo: true
});
function blocosAteOTrechoIncompleto(trechos: TrechoDoDocumentoDoCertificado[]): BlocoDocumento[] {
  const blocos: BlocoDocumento[] = [];
  for (const trecho of trechos) {
    blocos.push(...trecho.blocos);
    if (!trecho.completo) break;
  }
  return blocos;
}
const marcadorDeSecao = (numero: number): BlocoDocumento => ({
  id: `secao-${numero}`,
  manterComProximo: true,
  node: <MarcadorSecao numero={numero} secoes={SECOES} />
});
const blocoDeTitulo = (id: string, texto: string, icon: ReactNode): BlocoDocumento => ({
  id,
  manterComProximo: true,
  node: <div className="px-[37px] pt-2 pb-1">
      <DocTituloSecao icon={icon}>{texto}</DocTituloSecao>
    </div>
});
const blocoVazio = (id: string, texto: string): BlocoDocumento => ({
  id,
  node: <div className="px-[37px] pb-6">
      <div className="rounded-[8px] border border-neutral-200 bg-neutral-50 px-4 py-4 text-center text-[12.5px] text-neutral-500">
        {texto}
      </div>
    </div>
});
const blocosDaTabelaDeDados = (id: string, dados: [string, string][]): BlocoDocumento[] => blocosTabelaCertificado({
  id,
  cabecalhos: ['Informação', 'Registro'],
  colunas: 'grid-cols-[190px_minmax(0,1fr)]',
  linhas: dados.flatMap(([rotulo, valor], indice) => dividirTextoCertificado(valor, FATIA_DA_CELULA).map((parte, fatia) => ({
    id: `${indice}-${fatia}`,
    celulas: [fatia > 0 ? `${rotulo} · continuação` : rotulo, parte]
  })))
});
function blocosDaCapa(certificado: CertificadoNegociacaoResumoDto, urlValidacao: UrlDeValidacaoExibivel | null): BlocoDocumento[] {
  const {
    emissao
  } = certificado;
  return [{
    id: 'capa',
    manterComProximo: true,
    node: <SecaoCapa certificado={{
      dataEmissao: emissao.dataEmissao,
      numeroProcesso: numeroDaNegociacao(certificado),
      codigoAutenticacao: emissao.codigoAutenticacao
    }} eyebrow="Negociação com IA · Lexi" titulo="Certificado da Negociação" numeroLabel="Negociação Nº" dataLabel="Data de emissão · Brasília" dataValor={dataCertificado(emissao.dataEmissao)} />
  }, {
    id: 'sumario',
    node: <SumarioDocumento titulo="O que este certificado contém" descricao="O documento está organizado em cinco seções. Selecione uma para ir direto aos registros correspondentes." secoes={SECOES} />
  }, {
    id: 'capa-autenticidade',
    node: <SecaoAutenticidade codigoAutenticacao={emissao.codigoAutenticacao} urlAutenticacao={urlValidacao?.href ?? null} urlAutenticacaoExibicao={urlValidacao?.exibicao ?? null} />
  }];
}
function blocosDoObjetivoEEscopo(certificado: CertificadoNegociacaoResumoDto): BlocoDocumento[] {
  return [{
    id: 'escopo',
    node: <SecaoOQueEDocumento numeroProcesso={numeroDaNegociacao(certificado)} texto="Este certificado reúne os contatos, os acontecimentos e as conversas da negociação. A transcrição preserva o conteúdo literal das mensagens, organizado por conversa e por número utilizado. Mensagens trocadas nos mesmos números antes do início da negociação não fazem parte deste certificado." />
  }, {
    id: 'objetivo',
    node: <SecaoObjetivo titulo="O que estes registros demonstram?" paragrafos={['Verificação de número, envio, entrega, leitura, resposta, aceite e assinatura são fatos distintos. Cada informação deve corresponder ao registro de origem, sem presumir identidade ou ciência a partir de outro evento.']} />
  }];
}
function blocosDosCamposPersonalizados(camposPersonalizados: CampoPersonalizadoDoCertificado[]): BlocoDocumento[] {
  if (!camposPersonalizados.length) return [];
  return [blocoDeTitulo('campos-personalizados-titulo', 'Campos personalizados', <FileText className="size-4" />), ...blocosDaTabelaDeDados('campos-personalizados', camposPersonalizados.map(campo => [campo.rotulo, campo.valorFormatado]))];
}
const blocosDaIdentificacao = (certificado: CertificadoNegociacaoResumoDto): BlocoDocumento[] => [blocoDeTitulo('identificacao-titulo', 'Identificação da negociação', <FileText className="size-4" />), ...blocosDaTabelaDeDados('identificacao', [['Negociação', numeroDaNegociacao(certificado)], ['Assunto', certificado.caso.assunto]])];
const blocoDosParticipantes = ({
  pessoa
}: CertificadoNegociacaoResumoDto, contatos: ContatoDoCertificado[]): BlocoDocumento => ({
  id: 'participantes',
  node: <div className="[&_div]:min-w-0 [&_div]:wrap-anywhere [&_div]:whitespace-normal">
      <TabelaPartesGrupo grupo={{
      titulo: 'Contraparte cadastrada',
      corDestaque: 'brand',
      partes: [{
        nome: pessoa.nome || 'Não informado',
        papelLabel: 'Contraparte',
        papelDestacado: true,
        contatos: contatosDaContraparte(contatos),
        documento: pessoa.documento ?? 'CPF/CNPJ não informado'
      }]
    }} />
    </div>
});
const NOTA_DOS_PARTICIPANTES: BlocoDocumento = {
  id: 'participantes-nota',
  node: <SecaoTextosIntro texto="A identificação do cadastro não confirma quem respondeu em cada número. Lexi e atendentes são identificados na autoria das mensagens, conforme os registros disponíveis." />
};

/**
 * A tabela de participantes lista todos os WhatsApps da contraparte, e por isso espera a seção de
 * contatos inteira: com parte dela, o documento afirmaria uma lista incompleta.
 */
function trechosDoCasoEParticipantes(certificado: CertificadoNegociacaoResumoDto, {
  campos,
  contatos
}: SecoesDoDocumentoDoCertificado): TrechoDoDocumentoDoCertificado[] {
  return [trechoCompleto([marcadorDeSecao(2), ...blocosDaIdentificacao(certificado)]), {
    blocos: contatos.completa ? [blocoDosParticipantes(certificado, contatos.items)] : [],
    completo: contatos.completa
  }, {
    blocos: blocosDosCamposPersonalizados(campos.items),
    completo: campos.completa
  }, trechoCompleto([NOTA_DOS_PARTICIPANTES])];
}
function dadosDoContato(contato: ContatoDoCertificado): [string, string][] {
  const dados: [string, string][] = [['Situação', contato.situacaoRotulo], ['Tentativas registradas', contato.tentativas === null ? 'Não informadas' : String(contato.tentativas)], ['Verificação do porteiro', contato.porteiroRotulo], ['Verificado em · Brasília', dataCertificado(contato.porteiro?.verificadoEm)]];
  if (!contato.indicadoPor) return dados;
  return [...dados, ['Indicado por', formatTelefoneComCodigoPais(contato.indicadoPor.numero)]];
}

/** "Contato N" é a posição do contato na emissão inteira, que as páginas mantêm ao acrescentar. */
function blocosDosContatos({
  items: contatos,
  completa
}: SecaoDoDocumentoDoCertificado<ContatoDoCertificado>): BlocoDocumento[] {
  if (!contatos.length && completa) return [blocoVazio('sem-contatos', 'Nenhum número registrado nas fontes disponíveis.')];
  return contatos.flatMap((contato, index) => [blocoDeTitulo(`contato-titulo-${index}`, `Contato ${index + 1} · ${formatTelefoneComCodigoPais(contato.numero)}`, <Phone className="size-4" />), ...blocosDaTabelaDeDados(`contato-${index}`, dadosDoContato(contato))]);
}

// O marco congelado não tem id (CA-15); o MarcoCard só usa o id como chave, então a posição basta.
const marcosDoCard = (marcos: MarcoDoCertificado[]): MarcoNegociacao[] => marcos.map((marco, indice) => ({
  ...marco,
  id: String(indice),
  descricao: marco.descricao ?? '',
  link: null
}));
function blocosDoMarco(marco: MarcoNegociacao, ultimoDoDia: boolean): BlocoDocumento[] {
  const descricoes = marco.descricao ? dividirTextoCertificado(marco.descricao, FATIA_DA_DESCRICAO_DO_MARCO) : [''];
  return descricoes.map((descricao, fatia) => ({
    id: `marco-${marco.id}-${fatia}`,
    node: <div className="px-[37px]">
        <MarcoCard marco={{
        ...marco,
        descricao
      }} isLast={ultimoDoDia} />
      </div>
  }));
}

// A ordem é a recebida: o core já entrega os marcos em ordem crescente por (createdAt, id).
const blocosDosDiasDeMarcos = (marcos: MarcoNegociacao[]): BlocoDocumento[] => agruparMarcosPorDia(marcos).flatMap((grupo, indiceGrupo) => [{
  id: `marcos-dia-${indiceGrupo}`,
  manterComProximo: true,
  node: <div className="px-[37px] pt-5 pb-2">
          <div className="flex items-center gap-3">
            <span className="rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">
              {grupo.rotulo}
            </span>
            <span className="flex-1 border-t border-neutral-200" />
          </div>
        </div>
}, ...grupo.items.flatMap((marco, indice) => blocosDoMarco(marco, indice === grupo.items.length - 1))]);
function blocosDosMarcos({
  items,
  completa
}: SecaoDoDocumentoDoCertificado<MarcoDoCertificado>): BlocoDocumento[] {
  const marcos = marcosDoCard(items);
  const semMarcos = completa ? [blocoVazio('sem-marcos', 'Nenhum marco registrado ainda.')] : [];
  return [{
    id: 'marcos-titulo',
    manterComProximo: true,
    node: <div className="px-[37px] pt-2 pb-4">
          <h3 className="text-default-font border-b border-neutral-100 pb-4 text-[16px] font-semibold">
            Marcos da Negociação
          </h3>
          <p className="text-subtext-color pt-2 text-[12px]">
            Eventos importantes do fluxo, como ofertas, decisões e acordos.
          </p>
        </div>
  }, ...(marcos.length ? blocosDosDiasDeMarcos(marcos) : semMarcos)];
}
function blocosDasConversas({
  items: conversas,
  completa
}: SecaoDoDocumentoDoCertificado<ConversaDoCertificado>): BlocoDocumento[] {
  const semMensagens = completa && !conversas.some(conversa => conversa.mensagens.length);
  return [{
    id: 'conversa-intro',
    manterComProximo: true,
    node: <SecaoTextosIntro texto="Cada conversa reúne a sequência completa de mensagens daquele contato, incluindo respostas, intervenções humanas e tentativas que falharam. A mudança de número inicia outro bloco de conversa e preserva o anterior." />
  }, ...(semMensagens ? [blocoVazio('sem-conversas', 'Nenhuma conversa disponível nas fontes consultadas.')] : []), ...blocosConversasCertificado(conversas, completa)];
}
function blocosDaSeguranca(): BlocoDocumento[] {
  return [{
    id: 'seguranca',
    node: <SecaoObjetivo titulo="Autenticidade desde a emissão" paragrafos={['O certificado é emitido já autenticado, com seu código de conferência. A consulta pública verifica a autenticidade do documento recebido; ela não autentica o certificado posteriormente.', 'Uma nova emissão incorpora os registros mais recentes sem alterar a referência de uma versão já emitida.']} />
  }];
}

/**
 * O documento cresce conforme as páginas das seções chegam (ACC-02), sempre na ordem dele: o que
 * vem depois de uma seção incompleta só aparece quando ela termina, e o estado vazio de uma seção
 * só aparece quando o core disse que não há mais nada nela.
 */
export function CertificadoNegociacaoDocumento(t0) {
  const $ = _c(8);
  const {
    certificado,
    secoes
  } = t0;
  let t1;
  let urlValidacao;
  if ($[0] !== certificado || $[1] !== secoes) {
    urlValidacao = urlDeValidacaoExibivel(certificado.emissao.urlValidacao);
    t1 = blocosAteOTrechoIncompleto([trechoCompleto(blocosDaCapa(certificado, urlValidacao)), trechoCompleto([marcadorDeSecao(1), ...blocosDoObjetivoEEscopo(certificado)]), ...trechosDoCasoEParticipantes(certificado, secoes), {
      blocos: [marcadorDeSecao(3), ...blocosDosContatos(secoes.contatos)],
      completo: secoes.contatos.completa
    }, {
      blocos: blocosDosMarcos(secoes.marcos),
      completo: secoes.marcos.completa
    }, {
      blocos: [marcadorDeSecao(4), ...blocosDasConversas(secoes.conversas)],
      completo: secoes.conversas.completa
    }, trechoCompleto([marcadorDeSecao(5), ...blocosDaSeguranca()])]);
    $[0] = certificado;
    $[1] = secoes;
    $[2] = t1;
    $[3] = urlValidacao;
  } else {
    t1 = $[2];
    urlValidacao = $[3];
  }
  const blocos = t1;
  const t2 = urlValidacao?.exibicao ?? null;
  let t3;
  if ($[4] !== blocos || $[5] !== certificado.emissao.codigoAutenticacao || $[6] !== t2) {
    t3 = <DocumentoPaginado blocos={blocos} codigoAutenticacao={certificado.emissao.codigoAutenticacao} urlAutenticacaoExibicao={t2} />;
    $[4] = blocos;
    $[5] = certificado.emissao.codigoAutenticacao;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}